import React, { useState } from 'react';
import {
  SupportedLanguage,
  VocabularyLevel,
  QuestionType,
  MultipleChoiceSetupState,
  LANGUAGE_CONFIG,
  LEVEL_CONFIG,
  QUESTION_TYPE_CONFIG,
  initializeSetupState
} from '../utils/multipleChoiceLogic';
import { isVocabularyAvailable, getVocabulary } from '../data/vocabulary';
import WrongAnswerNotebook from './WrongAnswerNotebook';
import '../styles/MultipleChoiceSetup.css';

interface MultipleChoiceSetupProps {
  onStartGame: (setup: MultipleChoiceSetupState) => void;
  onBack: () => void;
}

const MultipleChoiceSetup: React.FC<MultipleChoiceSetupProps> = ({ onStartGame, onBack }) => {
  const [setup, setSetup] = useState<MultipleChoiceSetupState>(initializeSetupState());
  const [showNotebook, setShowNotebook] = useState(false);

  const handleLanguageSelect = (language: SupportedLanguage) => {
    setSetup(prev => ({
      ...prev,
      language,
      step: 'level',
      selectedTypes: [], // Reset types when language changes
      level: null
    }));
  };

  const handleLevelSelect = (level: VocabularyLevel) => {
    // Get supported question types for this language
    const vocab = getVocabulary(setup.language!, level);
    const defaultTypes: QuestionType[] = vocab
      ? ['target-to-english', 'english-to-target']
      : [];

    setSetup(prev => ({
      ...prev,
      level,
      step: 'types',
      selectedTypes: defaultTypes
    }));
  };

  const handleTypeToggle = (type: QuestionType) => {
    setSetup(prev => {
      const newTypes = prev.selectedTypes.includes(type)
        ? prev.selectedTypes.filter(t => t !== type)
        : [...prev.selectedTypes, type];
      return { ...prev, selectedTypes: newTypes };
    });
  };

  const handleCountSelect = (count: number) => {
    setSetup(prev => ({ ...prev, questionCount: count }));
  };

  const handleContinueToCount = () => {
    if (setup.selectedTypes.length > 0) {
      setSetup(prev => ({ ...prev, step: 'count' }));
    }
  };

  const handleStartGame = () => {
    if (setup.language && setup.level && setup.selectedTypes.length > 0) {
      onStartGame({ ...setup, step: 'ready' });
    }
  };

  const handleBackStep = () => {
    switch (setup.step) {
      case 'level':
        setSetup(prev => ({ ...prev, step: 'language', language: null }));
        break;
      case 'types':
        setSetup(prev => ({ ...prev, step: 'level', level: null }));
        break;
      case 'count':
        setSetup(prev => ({ ...prev, step: 'types' }));
        break;
      default:
        onBack();
    }
  };

  const getMaxQuestions = () => {
    if (!setup.language || !setup.level) return 50;
    const vocab = getVocabulary(setup.language, setup.level);
    return vocab?.items.length || 50;
  };

  // Render Step 1: Language Selection
  const renderLanguageStep = () => (
    <div className="setup-step">
      <h3 className="step-title">Select Language</h3>
      <p className="step-subtitle">Choose the language you want to practice</p>
      <div className="language-cards">
        {(Object.keys(LANGUAGE_CONFIG) as SupportedLanguage[]).map(lang => {
          const config = LANGUAGE_CONFIG[lang];
          const available = config.available && isVocabularyAvailable(lang);
          return (
            <div
              key={lang}
              className={`language-card ${!available ? 'disabled' : ''}`}
              onClick={() => available && handleLanguageSelect(lang)}
            >
              <div className="language-flag">{config.flag}</div>
              <div className="language-name">{config.name}</div>
              <div className="language-native">{config.nativeName}</div>
              {!available && <div className="coming-soon">Coming Soon</div>}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Render Step 2: Level Selection
  const renderLevelStep = () => (
    <div className="setup-step">
      <div className="selected-info">
        <span className="selected-flag">{LANGUAGE_CONFIG[setup.language!].flag}</span>
        <span className="selected-name">{LANGUAGE_CONFIG[setup.language!].name}</span>
      </div>
      <h3 className="step-title">Select Level</h3>
      <p className="step-subtitle">Choose your proficiency level</p>
      <div className="level-cards">
        {(Object.keys(LEVEL_CONFIG) as VocabularyLevel[]).map(level => {
          const config = LEVEL_CONFIG[level];
          const available = isVocabularyAvailable(setup.language!, level);
          return (
            <div
              key={level}
              className={`level-card ${!available ? 'disabled' : ''}`}
              onClick={() => available && handleLevelSelect(level)}
            >
              <div className="level-icon">{config.icon}</div>
              <div className="level-name">{config.name}</div>
              <div className="level-description">{config.description}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Render Step 3: Question Type Selection
  const renderTypesStep = () => {
    const vocab = getVocabulary(setup.language!, setup.level!);
    const supportedTypes = vocab?.metadata.supportedModes || [];

    return (
      <div className="setup-step">
        <div className="selected-info">
          <span className="selected-flag">{LANGUAGE_CONFIG[setup.language!].flag}</span>
          <span className="selected-name">{LANGUAGE_CONFIG[setup.language!].name}</span>
          <span className="selected-divider">•</span>
          <span className="selected-level">{LEVEL_CONFIG[setup.level!].name}</span>
        </div>
        <h3 className="step-title">Select Question Types</h3>
        <p className="step-subtitle">Choose one or more question formats</p>
        <div className="type-checkboxes">
          {supportedTypes.map(type => {
            const config = QUESTION_TYPE_CONFIG[type];
            const isSelected = setup.selectedTypes.includes(type);
            return (
              <label key={type} className={`type-checkbox ${isSelected ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleTypeToggle(type)}
                />
                <div className="type-content">
                  <div className="type-icon">{config.icon}</div>
                  <div className="type-info">
                    <div className="type-name">{config.name}</div>
                    <div className="type-description">{config.description}</div>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
        <button
          className="continue-button"
          onClick={handleContinueToCount}
          disabled={setup.selectedTypes.length === 0}
        >
          Continue
        </button>
      </div>
    );
  };

  // Render Step 4: Question Count Selection
  const renderCountStep = () => {
    const maxQuestions = getMaxQuestions();
    const countOptions = [10, 20, 30, maxQuestions].filter(n => n <= maxQuestions);

    return (
      <div className="setup-step">
        <div className="selected-info">
          <span className="selected-flag">{LANGUAGE_CONFIG[setup.language!].flag}</span>
          <span className="selected-name">{LANGUAGE_CONFIG[setup.language!].name}</span>
          <span className="selected-divider">•</span>
          <span className="selected-level">{LEVEL_CONFIG[setup.level!].name}</span>
        </div>
        <h3 className="step-title">Number of Questions</h3>
        <p className="step-subtitle">How many questions would you like?</p>
        <div className="count-buttons">
          {countOptions.map((count, index) => (
            <button
              key={count}
              className={`count-button ${setup.questionCount === count ? 'selected' : ''}`}
              onClick={() => handleCountSelect(count)}
            >
              {index === countOptions.length - 1 && count === maxQuestions ? 'All' : count}
            </button>
          ))}
        </div>
        <div className="selected-types-summary">
          <span className="summary-label">Question types:</span>
          <span className="summary-value">
            {setup.selectedTypes.map(t => QUESTION_TYPE_CONFIG[t].icon).join(' ')}
          </span>
        </div>
        <button
          className="start-game-button"
          onClick={handleStartGame}
        >
          Start Game
        </button>
      </div>
    );
  };

  // Show notebook if toggled
  if (showNotebook) {
    return (
      <WrongAnswerNotebook
        onClose={() => setShowNotebook(false)}
      />
    );
  }

  return (
    <div className="multiple-choice-setup">
      <div className="setup-header-buttons">
        <button className="back-button-top" onClick={handleBackStep}>
          ← Back
        </button>
        <button className="notebook-button" onClick={() => setShowNotebook(true)}>
          📓 Notebook
        </button>
      </div>

      <h2 className="setup-title">Multiple Choice</h2>
      <p className="setup-subtitle">Language Learning Quiz</p>

      <div className="step-indicator">
        <div className={`step-dot ${setup.step === 'language' ? 'active' : 'completed'}`}>1</div>
        <div className="step-line"></div>
        <div className={`step-dot ${setup.step === 'level' ? 'active' : setup.language ? 'completed' : ''}`}>2</div>
        <div className="step-line"></div>
        <div className={`step-dot ${setup.step === 'types' ? 'active' : setup.level ? 'completed' : ''}`}>3</div>
        <div className="step-line"></div>
        <div className={`step-dot ${setup.step === 'count' ? 'active' : ''}`}>4</div>
      </div>

      {setup.step === 'language' && renderLanguageStep()}
      {setup.step === 'level' && renderLevelStep()}
      {setup.step === 'types' && renderTypesStep()}
      {setup.step === 'count' && renderCountStep()}
    </div>
  );
};

export default MultipleChoiceSetup;
